import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  RefreshCw, ShieldCheck, Save, CheckCircle, AlertCircle,
   Layers, Search, Check, X, Briefcase
} from 'lucide-react';
import apiClient from '../api/client';
import { useAuth } from '../AuthContext';
import './DashboardBultos.css';
import './Pedidos.css';

/* ---------- MÓDULOS DEL SISTEMA ---------- */
const PAGE_GROUPS = [
  {
    group: 'Gestión de Ventas',
    color: '#60a5fa',
    pages: [
      { path: '/report', name: 'Reporte General' },
      { path: '/daily', name: 'Daily' },
      { path: '/dprincipal', name: 'Daily Principal' },
      { path: '/bultos', name: 'Bultos' },
      { path: '/usdsales', name: 'Ventas en USD' },
      { path: '/asesores', name: 'Dashboard Asesores' },
      { path: '/inventario', name: 'Dashboard Inventario' },
      { path: '/activacion', name: 'Dashboard Activación' },
      { path: '/sugeridos', name: 'Sugeridos' },
      { path: '/farmatodo', name: 'Farmatodo' },
      { path: '/surtido', name: 'Surtido' },
      { path: '/sellout', name: 'SellOut' },
      { path: '/sellout-moderno', name: 'SellOut Moderno' },
      { path: '/sellout-tradicional', name: 'SellOut Tradicional' },
      { path: '/catalogo', name: 'Catalogo' },
    ]
  },
  {
    group: 'Almacén',
    color: '#38bdf8',
    pages: [
      { path: '/almacen', name: 'Almacen' },
      { path: '/inventarioalmacen', name: 'Inventario Almacén' },
      { path: '/panelalmacen', name: 'Panel Almacen' },
      { path: '/consolidaciondespacho', name: 'Consolidación de Despacho' },
      { path: '/almacennotasentrega', name: 'Notas de Entrega Almacen' },
      { path: '/notascalmacen', name: 'Devolucion Almacen' },
    ]
  },
  {
    group: 'Cobranzas',
    color: '#fb923c',
    pages: [
      { path: '/cobranzas', name: 'Menú de Cobranzas' },
      { path: '/reportcob', name: 'Reporte de Cobranzas' },
      { path: '/detallecob', name: 'Detalle de Cobranzas' },
      { path: '/cxccob', name: 'Cuentas por Cobrar' },
    ]
  },
  {
    group: 'Administración de Ventas',
    color: '#34d399',
    pages: [
      { path: '/facturacion', name: 'Facturación' },
      { path: '/kpi-fact', name: 'KPI Facturación' },
      { path: '/detalle-fact', name: 'Detalle Facturación' },
      { path: '/detalle-notascredito', name: 'Detalle Notas de Crédito' },
      { path: '/panel-notascredito', name: 'Panel Notas de Crédito' },
      { path: '/reportesav', name: 'Reportes AV' },
      { path: '/pedidos', name: 'Pedidos' },
      { path: '/pedidosmasivos', name: 'Pedidos Masivos' },
    ]
  },
  {
    group: 'Trade Marketing',
    color: '#fbbf24',
    pages: [
      { path: '/tradeform', name: 'Trade Form (Solicitudes)' },
      { path: '/tradetrack', name: 'Trade Track (Seguimientos)' },
      { path: '/tradeflow', name: 'Trade Flow' },
    ]
  },
  {
    group: 'Talento Humano',
    color: '#a78bfa',
    pages: [
      { path: '/talentohumano', name: 'Talento Humano' },
      { path: '/reclutamiento', name: 'Reclutamiento' },
    ]
  }
];

/* ---------- TOAST ---------- */
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
};

const ToastContainer = ({ toasts }) => (
  <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '8px' }}>
    {toasts.map(t => (
      <div key={t.id} style={{
        padding: '12px 18px', borderRadius: '10px', fontWeight: 600, fontSize: '0.875rem',
        background: t.type === 'success' ? 'rgba(0,210,100,0.15)' : 'rgba(239,68,68,0.15)',
        color: t.type === 'success' ? '#00d26a' : '#f87171',
        border: `1px solid ${t.type === 'success' ? 'rgba(0,210,100,0.3)' : 'rgba(239,68,68,0.3)'}`,
        display: 'flex', alignItems: 'center', gap: '8px',
        backdropFilter: 'blur(10px)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        {t.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
        {t.message}
      </div>
    ))}
  </div>
);

/* ---------- TOGGLE SWITCH ---------- */
const Toggle = ({ checked, partial, onChange }) => (
  <div
    onClick={e => { e.stopPropagation(); onChange(); }}
    style={{
      width: '38px', height: '20px', borderRadius: '20px', flexShrink: 0,
      background: checked ? '#3b82f6' : partial ? 'rgba(251,191,36,0.35)' : 'rgba(255,255,255,0.1)',
      border: `1px solid ${checked ? '#3b82f6' : partial ? '#fbbf24' : 'rgba(255,255,255,0.1)'}`,
      position: 'relative', cursor: 'pointer', transition: 'all 0.22s'
    }}
  >
    <div style={{
      width: '14px', height: '14px', borderRadius: '50%',
      background: checked ? 'white' : partial ? '#fbbf24' : 'rgba(255,255,255,0.35)',
      position: 'absolute', top: '2px',
      left: checked ? '20px' : '2px',
      transition: 'left 0.22s, background 0.22s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
    }} />
  </div>
);

/* ================================================================ */
/* COMPONENTE PRINCIPAL                                              */
/* ================================================================ */
export default function AdminPermisos() {
  const { profile } = useAuth();
  const { toasts, addToast } = useToast();

  const [permissionsCache, setPermissionsCache] = useState({});
  const [selectedCargos, setSelectedCargos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [cargos, setCargos] = useState([]);
  const [loadingCargos, setLoadingCargos] = useState(true);

  const isMaster = profile?.cargo === 'Coordinador de Inteligencia de Negocio';

  // Cargar cargos desde la BD
  const fetchCargos = useCallback(async () => {
    setLoadingCargos(true);
    try {
      const { data } = await apiClient.get('/usuarios/cargos');
      setCargos(data || []);
    } catch (error) {
      console.error('Error cargando cargos:', error);
      addToast('Error al cargar los cargos', 'error');
    } finally {
      setLoadingCargos(false);
    }
  }, [addToast]);

  // Cargar permisos por cargo desde la BD
  const fetchAllPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/usuarios/permisos-por-cargo');
      setPermissionsCache(data || {});
    } catch {
      addToast('Error al cargar permisos', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    if (isMaster) {
      fetchCargos();
      fetchAllPermissions();
    }
  }, [isMaster, fetchCargos, fetchAllPermissions]);

  const filteredRoles = cargos.filter(r =>
    r.toLowerCase().includes(search.toLowerCase())
  );

  const toggleCargo = (role) => {
    setSelectedCargos(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const toggleAllFiltered = () => {
    const allSelected = filteredRoles.every(r => selectedCargos.includes(r));
    setSelectedCargos(prev =>
      allSelected
        ? prev.filter(r => !filteredRoles.includes(r))
        : [...new Set([...prev, ...filteredRoles])]
    );
  };

  const isChecked = (path) =>
    selectedCargos.length > 0 &&
    selectedCargos.every(c => {
      const p = permissionsCache[c] || [];
      return p.includes('*') || p.includes(path);
    });

  const isPartial = (path) => {
    if (selectedCargos.length <= 1) return false;
    const count = selectedCargos.filter(c => {
      const p = permissionsCache[c] || [];
      return p.includes('*') || p.includes(path);
    }).length;
    return count > 0 && count < selectedCargos.length;
  };

  const handleToggle = (path) => {
    if (!selectedCargos.length) return;
    const checked = isChecked(path);
    setPermissionsCache(prev => {
      const next = { ...prev };
      selectedCargos.forEach(cargo => {
        const cur = next[cargo] || [];
        next[cargo] = checked
          ? cur.filter(p => p !== path && p !== '*')
          : cur.includes(path) ? cur : [...cur, path];
      });
      return next;
    });
  };

  const handleToggleGroup = (pages) => {
    if (!selectedCargos.length) return;
    const allOn = pages.every(p => isChecked(p.path));
    setPermissionsCache(prev => {
      const next = { ...prev };
      selectedCargos.forEach(cargo => {
        const cur = next[cargo] || [];
        if (allOn) {
          next[cargo] = cur.filter(p => !pages.some(gp => gp.path === p));
        } else {
          const toAdd = pages.map(p => p.path).filter(p => !cur.includes(p));
          next[cargo] = [...cur, ...toAdd];
        }
      });
      return next;
    });
  };

  // Guardar permisos para los cargos seleccionados
  const handleSave = async () => {
    if (!selectedCargos.length) return;
    setSaving(true);
    try {
      const payload = {};
      selectedCargos.forEach(cargo => {
        let paths = permissionsCache[cargo] || [];
        // SIEMPRE incluir /home
        if (!paths.includes('/home')) {
          paths = ['/home', ...paths];
        }
        payload[cargo] = paths;
      });
      
      await apiClient.post('/usuarios/update-permisos-multiples-cargos', payload);
      
      addToast(`Permisos guardados para ${selectedCargos.length} cargo(s)`);
    } catch (error) {
      console.error('Error guardando:', error);
      addToast('Error al guardar permisos', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Dar acceso total a los seleccionados
  const handleGiveFullAccess = () => {
    if (!selectedCargos.length) return;
    const allHaveFullAccess = selectedCargos.every(c => 
      (permissionsCache[c] || []).includes('*')
    );
    
    setPermissionsCache(prev => {
      const next = { ...prev };
      selectedCargos.forEach(cargo => {
        next[cargo] = allHaveFullAccess ? ['/home'] : ['*'];
      });
      return next;
    });
  };

  if (!isMaster) {
    return (
      <div style={{ color: 'white', padding: '80px', textAlign: 'center', opacity: 0.5 }}>
        <ShieldCheck size={48} style={{ display: 'block', margin: '0 auto 16px' }} />
        <h2>Acceso Denegado</h2>
        <p style={{ marginTop: '8px', fontSize: '0.9rem' }}>
          Solo el Coordinador de Inteligencia de Negocio puede acceder a esta sección.
        </p>
      </div>
    );
  }

  const allFiltered = filteredRoles.length > 0 && filteredRoles.every(r => selectedCargos.includes(r));
  const allHaveFullAccess = selectedCargos.length > 0 && 
    selectedCargos.every(c => (permissionsCache[c] || []).includes('*'));

  return (
    <motion.div className="dashboard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <ToastContainer toasts={toasts} />

      {/* HEADER */}
      <header className="header-flex">
        <div className="title-section">
          <h1 className="dashboard-title">Administrar Accesos por Cargo</h1>
          <div className="title-underline"></div>
        </div>
        <div className="date-selectors">
          <button className="refresh-button" onClick={() => { fetchCargos(); fetchAllPermissions(); }} disabled={loading || loadingCargos}>
            <RefreshCw size={16} className={loading || loadingCargos ? 'spin-anim' : ''} />
          </button>
          {selectedCargos.length > 0 && (
            <>
              <button onClick={() => setSelectedCargos([])}
                style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.82rem' }}>
                <X size={13} /> Limpiar ({selectedCargos.length})
              </button>
              <button
                className="new-order-btn"
                onClick={handleSave}
                disabled={saving}
                style={{ background: '#16a34a' }}
              >
                <Save size={15} />
                <span style={{ marginLeft: '7px' }}>
                  {saving ? 'Guardando...' : `Guardar (${selectedCargos.length})`}
                </span>
              </button>
            </>
          )}
        </div>
      </header>

      {/* KPI CARDS */}
      <div className="dashboard-indicators3" style={{ padding: '15px', background: 'transparent' }}>
        <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper"><Briefcase size={22} color="white" /></div>
            <span className="indicator-label">Total Cargos</span>
            <div className="indicator-right-group"><span className="indicator-value">{cargos.length}</span></div>
          </div>
        </div>
        <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper"><Layers size={22} color="white" /></div>
            <span className="indicator-label">Módulos</span>
            <div className="indicator-right-group">
              <span className="indicator-value">{PAGE_GROUPS.reduce((a, g) => a + g.pages.length, 0)}</span>
            </div>
          </div>
        </div>
        <div className="indicator-card2 v-real">
          <div className="card-content">
            <div className="inven-icon-wrapper"><ShieldCheck size={22} color="white" /></div>
            <span className="indicator-label">Seleccionados</span>
            <div className="indicator-right-group">
              <span className="indicator-value" style={{ color: selectedCargos.length > 0 ? '#60a5fa' : 'inherit' }}>
                {selectedCargos.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* LAYOUT DOS COLUMNAS */}
      <div style={{ display: 'flex', gap: '16px', marginTop: '4px', alignItems: 'flex-start' }}>

        {/* ── PANEL IZQUIERDO: TABLA DE CARGOS ── */}
        <div className="detail-table-container" style={{ width: '320px', flexShrink: 0, marginTop: 0 }}>
          <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '12px 16px' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Cargos</h3>
            <label
              onClick={toggleAllFiltered}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.75rem', opacity: 0.65, userSelect: 'none' }}
            >
              <div style={{
                width: '15px', height: '15px', borderRadius: '3px', flexShrink: 0,
                border: `1px solid ${allFiltered ? '#3b82f6' : 'rgba(255,255,255,0.25)'}`,
                background: allFiltered ? '#3b82f6' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {allFiltered && <Check size={9} color="white" strokeWidth={3} />}
              </div>
              Todos
            </label>
          </div>

          <div style={{ padding: '0 12px 10px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={13} style={{ position: 'absolute', left: '9px', opacity: 0.4, pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Buscar cargo..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: '100%', paddingLeft: '28px', paddingRight: '10px',
                  paddingTop: '7px', paddingBottom: '7px',
                  border: '1px solid rgba(255,255,255,0.08)', borderRadius: '7px',
                  background: 'rgba(255,255,255,0.05)', color: 'var(--text-third)',
                  fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div className="responsive-table-wrapper" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {loadingCargos ? (
              <div style={{ padding: '40px', textAlign: 'center', opacity: 0.5 }}>
                <RefreshCw size={24} className="spin-anim" style={{ marginBottom: '8px' }} />
                <div style={{ fontSize: '0.85rem' }}>Cargando cargos...</div>
              </div>
            ) : filteredRoles.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', opacity: 0.4 }}>
                No se encontraron cargos
              </div>
            ) : (
              <table className="detail-table">
                <tbody>
                  {filteredRoles.map(role => {
                    const isSelected = selectedCargos.includes(role);
                    const configuredPaths = (permissionsCache[role] || []);
                    const hasFullAccess = configuredPaths.includes('*');
                    
                    return (
                      <tr
                        key={role}
                        onClick={() => toggleCargo(role)}
                        style={{
                          cursor: 'pointer',
                          background: isSelected ? 'rgba(59,130,246,0.1)' : 'transparent',
                          transition: 'background 0.15s'
                        }}
                      >
                        <td style={{ width: '32px', paddingRight: '4px' }}>
                          <div style={{
                            width: '15px', height: '15px', borderRadius: '3px', margin: 'auto',
                            border: `1px solid ${isSelected ? '#3b82f6' : 'rgba(255,255,255,0.2)'}`,
                            background: isSelected ? '#3b82f6' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.15s', flexShrink: 0
                          }}>
                            {isSelected && <Check size={9} color="white" strokeWidth={3} />}
                          </div>
                        </td>
                        <td>
                          <div style={{
                            fontSize: '0.8rem', fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? '#93c5fd' : 'var(--text-third)',
                            lineHeight: 1.35
                          }}>
                            {role}
                          </div>
                          <div style={{ fontSize: '0.68rem', marginTop: '2px' }}>
                            {hasFullAccess ? (
                              <span style={{ color: '#34d399' }}>✓ Acceso total</span>
                            ) : configuredPaths.length > 0 ? (
                              <span style={{ color: '#60a5fa' }}>{configuredPaths.length} módulos</span>
                            ) : (
                              <span style={{ color: '#f87171' }}>Sin acceso</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="table-footer-pagination" style={{ padding: '10px 16px', borderRadius: '0 0 12px 12px' }}>
            <span style={{ fontSize: '0.75rem', opacity: 0.45 }}>
              {loadingCargos ? 'Cargando...' : `${filteredRoles.length} cargos • ${selectedCargos.length} seleccionados`}
            </span>
          </div>
        </div>

        {/* ── PANEL DERECHO: TABLA DE MÓDULOS ── */}
        <div className="detail-table-container" style={{ flex: 1, marginTop: 0 }}>
          <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>
              {selectedCargos.length > 0 
                ? `Módulos para ${selectedCargos.length} cargo(s)`
                : 'Módulos del Sistema'}
            </h3>
            {selectedCargos.length > 0 && (
              <button
                onClick={handleGiveFullAccess}
                style={{
                  padding: '6px 12px', borderRadius: '6px', 
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: allHaveFullAccess ? 'rgba(239,68,68,0.1)' : 'rgba(52,211,153,0.1)',
                  color: allHaveFullAccess ? '#f87171' : '#34d399',
                  cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: '4px'
                }}
              >
                {allHaveFullAccess ? '✕ Quitar Acceso Total' : '✓ Dar Acceso Total'}
              </button>
            )}
          </div>

          {selectedCargos.length === 0 ? (
            <div style={{ padding: '80px 20px', textAlign: 'center', opacity: 0.3 }}>
              <Briefcase size={52} style={{ display: 'block', margin: '0 auto 16px' }} />
              <h3 style={{ margin: '0 0 8px' }}>Selecciona uno o más cargos</h3>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>Haz clic en los cargos de la tabla izquierda para configurar sus accesos.</p>
            </div>
          ) : (
            <div className="responsive-table-wrapper" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>Módulo</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>Acceso</th>
                  </tr>
                </thead>
                <tbody>
                  {PAGE_GROUPS.map(group => {
                    const allGroupOn = group.pages.every(p => isChecked(p.path));
                    const groupPartial = group.pages.some(p => isPartial(p.path));
                    
                    return (
                      <React.Fragment key={group.group}>
                        <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <td colSpan={2} style={{ padding: '10px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: group.color, flexShrink: 0 }} />
                                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: group.color }}>
                                  {group.group}
                                </span>
                                <span style={{ fontSize: '0.72rem', opacity: 0.4 }}>({group.pages.length} módulos)</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '0.72rem', opacity: 0.5 }}>
                                  {allGroupOn ? 'Quitar todos' : 'Activar todos'}
                                </span>
                                <Toggle
                                  checked={allGroupOn}
                                  partial={groupPartial}
                                  onChange={() => handleToggleGroup(group.pages)}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>

                        {group.pages.map(page => {
                          const checked = isChecked(page.path);
                          const partial = isPartial(page.path);
                          
                          return (
                            <tr
                              key={page.path}
                              onClick={() => handleToggle(page.path)}
                              style={{
                                cursor: 'pointer',
                                background: checked
                                  ? 'rgba(59,130,246,0.07)'
                                  : partial
                                    ? 'rgba(251,191,36,0.04)'
                                    : 'transparent',
                                transition: 'background 0.15s'
                              }}
                            >
                              <td style={{ paddingLeft: '32px' }}>
                                <div style={{
                                  fontSize: '0.85rem',
                                  fontWeight: checked ? 600 : 400,
                                  color: checked ? 'var(--in-table)' : 'var(--text-third)',
                                  transition: 'color 0.2s'
                                }}>
                                  {page.name}
                                </div>
                                {partial && (
                                  <div style={{ fontSize: '0.68rem', color: '#fbbf24', marginTop: '1px' }}>
                                    Parcial ({selectedCargos.filter(c => (permissionsCache[c] || []).includes(page.path)).length}/{selectedCargos.length} cargos)
                                  </div>
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <Toggle
                                  checked={checked}
                                  partial={partial}
                                  onChange={() => handleToggle(page.path)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="table-footer-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '0 0 12px 12px' }}>
            <span style={{ fontSize: '0.75rem', opacity: 0.4 }}>
              🔵 Todos tienen acceso &nbsp;|&nbsp; 🟡 Parcial &nbsp;|&nbsp; ⚫ Sin acceso
            </span>
            <span style={{ fontSize: '0.75rem', opacity: 0.4 }}>
              {selectedCargos.length > 0 && `${selectedCargos.length} cargo(s) seleccionado(s)`}
            </span>
          </div>
        </div>
      </div>

      <footer className="status-footer">
        {loading || loadingCargos ? 'CARGANDO...' : saving ? 'GUARDANDO...' :
          selectedCargos.length > 0
            ? `EDITANDO PERMISOS DE ${selectedCargos.length} CARGO(S)`
            : 'SELECCIONA UNO O MÁS CARGOS'}
      </footer>
    </motion.div>
  );
}